// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GridPreviewPopup.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * GridPreviewPopup component
 */
import React, { Component } from 'react';
import { propTypes as MobxPropTypes } from 'mobx-react';
import { observe } from 'mobx';
import { curry, isEmpty, omit } from 'lodash/fp';
import connect from 'share/enhancers/Izenda.Connect';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import actions from './Izenda.GridPreviewPopupActions';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import GridPreviewPopupMetric from './Izenda.GridPreviewPopupMetric';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import NoData from 'share/components/controls/Izenda.NoData';
import ReportPartGridVerticalHorizontal from './ReportPartGrid/Izenda.ReportPartGridVerticalHorizontal';
import ReportPartGridSeparator from './ReportPartGrid/Izenda.ReportPartGridSeparator';
import { GRID_PREVIEW_POPUP_SIZE } from 'logic/data/Izenda.ReportsConstants';
import deferComponent from 'share/components/hoc/Izenda.DeferComponent';

const LoadingComponent = () => (
  <div style={GRID_PREVIEW_POPUP_SIZE}>
    <ScrollBox>{ReportPartUtils.renderLazyLoadingBar()}</ScrollBox>
  </div>
);

const DeferredReportPartGridVerticalHorizontal = deferComponent(ReportPartGridVerticalHorizontal, LoadingComponent);

@connect(actions)
class GridPreviewPopup extends Component {
  static propTypes = {
    reportPart: MobxPropTypes.observableObject.isRequired
  };

  async componentDidMount() {
    const { reportPart, gridPreviewStore, buildGridData } = this.props;
    this.refreshDataObserver = observe(reportPart, 'queryData', buildGridData.bind(this));
    await buildGridData.apply(this);
    const { selectedMetricIndex } = gridPreviewStore;
    gridPreviewStore.updateShowingPartIndex(Math.max(0, selectedMetricIndex));
  }

  componentWillUnmount() {
    this.refreshDataObserver();
  }

  renderHeader = curry((props: any, container: any, fieldElement: any, title: any, callBack: any): any => {
    return title;
  });

  renderMainComponent = () => {
    const {
      props,
      props: {
        gridPreviewStore,
        gridPreviewStore: { gridReportParts }
      }
    } = this;
    const {
      partDefinition,
      partDefinition: { previewDataRecords }
    } = gridReportParts[gridPreviewStore.showingPartIndex];

    if (!isEmpty(previewDataRecords)) {
      const restProps = omit(['reportPart'], props);
      if (partDefinition.reportPartContent.useSeparator) {
        return (
          <ReportPartGridSeparator
            records={previewDataRecords}
            mainComponent={DeferredReportPartGridVerticalHorizontal}
            reportPart={partDefinition}
            {...restProps}
            staticRender={false}
            viewMode
            renderHeader={this.renderHeader(props)}
            isJsFormatEnabled
            isGridPreviewModal
          />
        );
      }
      return (
        <DeferredReportPartGridVerticalHorizontal
          records={previewDataRecords}
          reportPart={partDefinition}
          {...restProps}
          staticRender={false}
          viewMode
          renderHeader={this.renderHeader(props)}
          isJsFormatEnabled
          isGridPreviewModal
        />
      );
    }

    return props.reportPart.isLoadingData ? ReportPartUtils.renderLazyLoadingBar() : <NoData />;
  };

  /**
   * Render content with metric
   * @param {Object} containerStyle
   */
  renderWithMetric = (): any => {
    const {
      gridPreviewStore,
      gridPreviewStore: { gridReportParts }
    } = this.props;

    return (
      <div style={GRID_PREVIEW_POPUP_SIZE} className="izenda-GridPreviewPopup izenda-Wrapper">
        <GridPreviewPopupMetric
          onChange={this.props.onChangeGridPreviewPopupMetric}
          selectedPart={gridReportParts[gridPreviewStore.showingPartIndex]}
          gridReportParts={gridReportParts}
        />
        <div className="izenda-Wrapper-content">
          <ScrollBox>{this.renderMainComponent()}</ScrollBox>
        </div>
      </div>
    );
  };

  /**
   * Render content without metric
   */
  renderWithoutMetric = (): any => (
    <div className="izenda-GridPreviewPopup" style={GRID_PREVIEW_POPUP_SIZE}>
      <ScrollBox>{this.renderMainComponent()}</ScrollBox>
    </div>
  );

  /**
   * Render loading bar
   */
  renderLoadingBar = (): any => <LoadingComponent />;

  render() {
    const {
      gridPreviewStore,
      gridPreviewStore: { gridReportParts, selectedMetricIndex }
    } = this.props;
    const showMetric = gridReportParts.length > 1 && selectedMetricIndex < 0;
    let renderContentFn = this.renderLoadingBar;
    if (!gridPreviewStore.isProcessingData) {
      renderContentFn = showMetric ? this.renderWithMetric : this.renderWithoutMetric;
    }

    return (
      <Popup sizeClass="modal-lg grid-preview-modal" visible autoClose={false} onClosing={this.props.onClosing} type="none">
        <Popup.Header />
        <Popup.Body>{renderContentFn()}</Popup.Body>
      </Popup>
    );
  }
}
export default GridPreviewPopup;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/Izenda.GridPreviewPopup.js